import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Upload,
  Radio,
  message,
} from 'antd';
import { CameraOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import type { UploadRequestOption as RcCustomRequestOptions } from 'rc-upload/lib/interface';
import api, { uploadImage } from '../services/api';
import {
  collectBrowserContext,
  installErrorHandlers,
  attachAxiosInterceptor,
} from '../lib/bugContext';

const { TextArea } = Input;

// Версия сборки — Vite подставляет через build-arg
const APP_VERSION = (import.meta as any).env?.VITE_BUILD_COMMIT
  || (import.meta as any).env?.VITE_APP_VERSION
  || 'unknown';

// Ключи localStorage
const LS_TESTER_MODE = 'bug_tester_mode';
const LS_REPORTER_NAME = 'bug_reporter_name';
const LS_REPORTER_CONTACT = 'bug_reporter_contact';

type ReportType = 'bug' | 'feature';

// Устанавливаем глобальные перехватчики один раз при загрузке модуля
installErrorHandlers();
attachAxiosInterceptor(api);

/**
 * Виджет баг-репорта для тестировщиков.
 * Активируется URL-параметром ?tester=1, сохраняет режим в localStorage.
 * Плавающая кнопка 🐛 в правом нижнем углу.
 */
const AdminBugReportWidget: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [testerMode, setTesterMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [reportType, setReportType] = useState<ReportType>('bug');
  const [form] = Form.useForm();

  const isFeature = reportType === 'feature';

  // Проверяем URL-параметр ?tester=1 и localStorage
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('tester') === '1') {
      localStorage.setItem(LS_TESTER_MODE, '1');
      setTesterMode(true);
    } else if (localStorage.getItem(LS_TESTER_MODE) === '1') {
      setTesterMode(true);
    }
  }, []);

  // Предзаполняем сохранённые данные тестера
  useEffect(() => {
    if (visible) {
      const savedName = localStorage.getItem(LS_REPORTER_NAME) || '';
      const savedContact = localStorage.getItem(LS_REPORTER_CONTACT) || '';
      form.setFieldsValue({
        reporter_name: savedName,
        reporter_contact: savedContact,
      });
    }
  }, [visible, form]);

  // Кастомная загрузка скриншотов через uploadImage.
  // uploadImage() возвращает строку (URL), но AntD Upload и логика сабмита
  // ожидают объект с полем .url — оборачиваем результат.
  const handleCustomUpload = useCallback(async (options: RcCustomRequestOptions) => {
    const { file, onSuccess, onError } = options;
    try {
      const url = await uploadImage(file as File, 'bug-reports');
      if (onSuccess) onSuccess({ url });
    } catch (err) {
      if (onError) onError(err as Error);
    }
  }, []);

  // Отправка формы
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Сохраняем имя и контакт тестера
      if (values.reporter_name) {
        localStorage.setItem(LS_REPORTER_NAME, values.reporter_name);
      }
      if (values.reporter_contact) {
        localStorage.setItem(LS_REPORTER_CONTACT, values.reporter_contact);
      }

      // Собираем URL-ы скриншотов из загруженных файлов
      const screenshots = fileList
        .filter((f) => f.status === 'done' && f.response?.url)
        .map((f) => f.response.url);

      const tenantId = localStorage.getItem('tenant_id') || '1';

      // Собираем технический контекст (viewport, route, console/network errors, session_id)
      const ctx = collectBrowserContext();

      const payload = {
        // Тип и платформа
        report_type: reportType,
        platform: 'admin',
        // Основные поля
        title: values.title,
        description: values.description || '',
        priority: values.priority || 'medium',
        feature_benefit: isFeature ? (values.feature_benefit || '') : undefined,
        screenshots,
        reporter_name: values.reporter_name || '',
        reporter_contact: values.reporter_contact || '',
        // Автосбор
        ...ctx,
        app_version: APP_VERSION,
      };

      await api.post(`/bug-reports?tenant_id=${tenantId}`, payload);

      message.success(isFeature ? 'Фича предложена!' : 'Баг-репорт отправлен!');
      form.resetFields();
      setFileList([]);
      setVisible(false);
    } catch (err: any) {
      if (err?.errorFields) {
        // Ошибка валидации формы — не показываем message
        return;
      }
      message.error('Ошибка отправки обращения');
      console.error('Report submit error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Не показываем виджет если режим тестера не активен
  if (!testerMode) return null;

  return (
    <>
      {/* Плавающая кнопка */}
      <button
        onClick={() => setVisible(true)}
        title="Отправить обращение"
        style={{
          position: 'fixed',
          bottom: 80,
          right: 24,
          zIndex: 9999,
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: '#1890ff',
          color: '#fff',
          fontSize: 24,
          border: 'none',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        💬
      </button>

      {/* Модальное окно с формой */}
      <Modal
        title={isFeature ? '✨ Предложить фичу' : '🐛 Сообщить о баге'}
        open={visible}
        onCancel={() => setVisible(false)}
        onOk={handleSubmit}
        confirmLoading={loading}
        okText="Отправить"
        cancelText="Отмена"
        destroyOnClose
      >
        <Form form={form} layout="vertical" initialValues={{ priority: 'medium' }}>
          {/* Переключатель типа обращения */}
          <Form.Item label="Тип обращения">
            <Radio.Group
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              buttonStyle="solid"
              optionType="button"
            >
              <Radio.Button value="bug">🐛 Баг</Radio.Button>
              <Radio.Button value="feature">✨ Фича</Radio.Button>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="title"
            label={isFeature ? 'Название фичи' : 'Заголовок'}
            rules={[{ required: true, message: 'Укажите заголовок' }]}
          >
            <Input placeholder={isFeature ? 'Что хотите добавить?' : 'Кратко опишите проблему'} />
          </Form.Item>

          <Form.Item
            name="description"
            label={isFeature ? 'Описание (какая проблема / сценарий)' : 'Описание'}
          >
            <TextArea
              rows={4}
              placeholder={isFeature
                ? 'С какой ситуацией столкнулись? Что хотели бы уметь?'
                : 'Подробное описание: шаги воспроизведения, ожидание, результат'}
            />
          </Form.Item>

          {isFeature && (
            <Form.Item name="feature_benefit" label="Какую проблему решит эта фича?">
              <TextArea rows={3} placeholder="Какую пользу даст пользователям / бизнесу" />
            </Form.Item>
          )}

          <Form.Item name="priority" label={isFeature ? 'Значимость' : 'Приоритет'}>
            <Select>
              <Select.Option value="low">Низкий</Select.Option>
              <Select.Option value="medium">Средний</Select.Option>
              <Select.Option value="high">Высокий</Select.Option>
              <Select.Option value="critical">Критический</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label="Скриншоты">
            <Upload
              listType="picture-card"
              fileList={fileList}
              onChange={({ fileList: newList }) => setFileList(newList)}
              customRequest={handleCustomUpload}
              accept="image/*"
              multiple
            >
              {fileList.length < 5 && (
                <div>
                  <CameraOutlined />
                  <div style={{ marginTop: 8 }}>Загрузить</div>
                </div>
              )}
            </Upload>
          </Form.Item>

          <Form.Item name="reporter_name" label="Имя тестера">
            <Input placeholder="Ваше имя" />
          </Form.Item>

          <Form.Item name="reporter_contact" label="Контакт">
            <Input placeholder="Telegram, email или телефон" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default AdminBugReportWidget;
